import React, { useState, useEffect } from 'react';
import MainLayout from '../layout/MainLayout';
import Card, { CardHeader, CardTitle, CardBody } from '../core/Card/Card';
import Badge from '../core/Badge/Badge';
import CreateMentorModal from './CreateMentorModal';
import CreateMenteeModal from './CreateMenteeModal';
import EditUserModal from './EditUserModal';
import {
  Users, UserPlus, Search, Filter, CheckCircle, XCircle,
  Edit, Trash2, Mail, Calendar, Shield, GraduationCap
} from 'lucide-react';
import api from '../../services/api';
import config from '../../config';
import { User } from '../../types';
import '../../styles/admin-users.scss';

interface UserWithProfile extends User {
  phone?: string;
  company?: string;
  title?: string;
}

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<UserWithProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreateMentorModal, setShowCreateMentorModal] = useState(false);
  const [showCreateMenteeModal, setShowCreateMenteeModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserWithProfile | null>(null);

  // Helper to construct full image URL using env variable
  const getImageUrl = (imagePath: string | null) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http')) return imagePath;
    // Use API base URL without /api suffix to get backend root URL
    const baseUrl = config.api.baseUrl.replace(/\/api\/?$/, '');
    return `${baseUrl}/${imagePath}`;
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleFilter, statusFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (roleFilter !== 'all') params.role = roleFilter;
      if (statusFilter !== 'all') params.status = statusFilter;

      const response = await api.admin.getUsers(params);

      if (response.data.success) {
        const usersData = Array.isArray(response.data.data)
          ? response.data.data
          : response.data.data?.users || [];
        setUsers(usersData);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId: number) => {
    if (!window.confirm('¿Aprobar este usuario?')) return;

    try {
      await api.admin.approveUser(userId);
      await loadUsers();
    } catch (error) {
      console.error('Error approving user:', error);
      alert('Error al aprobar usuario');
    }
  };

  const handleRejectUser = async (userId: number) => {
    if (!window.confirm('¿Rechazar este usuario? Esta acción no se puede deshacer.')) return;

    try {
      await api.admin.rejectUser(userId);
      await loadUsers();
    } catch (error) {
      console.error('Error rejecting user:', error);
      alert('Error al rechazar usuario');
    }
  };

  const handleEditUser = (user: UserWithProfile) => {
    setSelectedUser(user);
    setShowEditModal(true);
  };

  const handleDeleteUser = async (userId: number) => {
    if (!window.confirm('¿Está seguro de eliminar este usuario? Esta acción no se puede deshacer.')) return;

    try {
      await api.delete(`/users/${userId}`);
      await loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Error al eliminar usuario');
    }
  };

  const getRoleBadge = (roleName: string) => {
    switch (roleName) {
      case 'super_admin':
        return <Badge variant="danger"><Shield size={12} /> Super Admin</Badge>;
      case 'admin':
        return <Badge variant="warning"><Shield size={12} /> Admin</Badge>;
      case 'mentor':
        return <Badge variant="success">Mentor</Badge>;
      case 'mentee':
        return <Badge variant="info">Mentee</Badge>;
      default:
        return <Badge variant="default">{roleName}</Badge>;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="success"><CheckCircle size={12} /> Activo</Badge>;
      case 'inactive':
        return <Badge variant="default">Inactivo</Badge>;
      case 'pending_approval':
        return <Badge variant="warning">Pendiente</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = searchTerm === '' ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (u as any).username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.last_name?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const stats = {
    total: users.length,
    active: users.filter(u => u.status === 'active').length,
    pending: users.filter(u => u.status === 'pending_approval').length,
    mentors: users.filter(u => u.role_name === 'mentor').length,
    mentees: users.filter(u => u.role_name === 'mentee').length
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="admin-users admin-users--loading">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4">Cargando usuarios...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="admin-users">
        {/* Page Header */}
        <div className="page-header">
          <div className="page-header__info">
            <div className="eyebrow">{(() => { const d = new Date(); return `${d.toLocaleDateString('es', { weekday: 'long' })} ${d.getDate()} de ${d.toLocaleDateString('es', { month: 'short' }).replace('.', '')} · Administrador`.toUpperCase(); })()}</div>
            <h1 className="heading-1">Usuarios del <em>sistema</em></h1>
            <p className="page-meta">
              {stats.total} usuarios · {stats.active} activos · {stats.pending} pendientes
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon stat-icon--navy">
              <Users size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.total}</div>
              <div className="stat-label">Total Usuarios</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon stat-icon--green">
              <CheckCircle size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.active}</div>
              <div className="stat-label">Activos</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon stat-icon--red">
              <Shield size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.mentors}</div>
              <div className="stat-label">Mentores</div>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon stat-icon--gold">
              <Users size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{stats.mentees}</div>
              <div className="stat-label">Mentees</div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <Card animate>
          <CardBody>
            <div className="users-controls">
              <div className="search-box">
                <Search size={20} />
                <input
                  type="text"
                  placeholder="Buscar por nombre, usuario o email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>

              <div className="filters">
                <div className="filter-group">
                  <Filter size={16} />
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                  >
                    <option value="all">Todos los roles</option>
                    <option value="super_admin">Super Admin</option>
                    <option value="admin">Admin</option>
                    <option value="mentor">Mentor</option>
                    <option value="mentee">Mentee</option>
                  </select>
                </div>

                <div className="filter-group">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="all">Todos los estados</option>
                    <option value="active">Activo</option>
                    <option value="inactive">Inactivo</option>
                    <option value="pending_approval">Pendiente</option>
                  </select>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Users Table */}
        <Card animate animateDelay={0.1}>
          <CardHeader>
            <CardTitle>
              Lista de Usuarios ({filteredUsers.length})
            </CardTitle>
          </CardHeader>
          <CardBody>
            <div className="users-table-wrapper">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Usuario</th>
                    <th>Email</th>
                    <th>Rol</th>
                    <th>Estado</th>
                    <th>Registro</th>
                    <th>Último Acceso</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center">
                        <div className="empty-state">
                          <Users size={48} />
                          <p>No se encontraron usuarios</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredUsers.map((u) => (
                      <tr key={u.id}>
                        <td>
                          <div className="user-cell">
                            <div className="user-avatar">
                              {u.profile_image ? (
                                <img src={getImageUrl(u.profile_image) || undefined} alt={u.first_name} />
                              ) : (
                                <div className="avatar-placeholder">
                                  {u.first_name?.[0]}{u.last_name?.[0]}
                                </div>
                              )}
                            </div>
                            <div className="user-info">
                              <div className="user-name">
                                {u.first_name} {u.last_name}
                              </div>
                              {u.title && (
                                <div className="user-title">{u.title}</div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="email-cell">
                            <Mail size={14} />
                            {u.email}
                          </div>
                          {(u as any).username && (
                            <div className="username-cell">usuario: {(u as any).username}</div>
                          )}
                        </td>
                        <td>{getRoleBadge(u.role_name)}</td>
                        <td>{getStatusBadge(u.status)}</td>
                        <td>
                          <div className="date-cell">
                            <Calendar size={14} />
                            {new Date(u.created_at).toLocaleDateString('es-ES')}
                          </div>
                        </td>
                        <td>
                          {u.last_login ? (
                            <div className="date-cell">
                              {new Date(u.last_login).toLocaleDateString('es-ES')}
                            </div>
                          ) : (
                            <span className="text-muted">Nunca</span>
                          )}
                        </td>
                        <td>
                          <div className="action-buttons">
                            {u.status === 'pending_approval' && (
                              <>
                                <button
                                  className="btn-icon btn-icon--success"
                                  onClick={() => handleApproveUser(u.id)}
                                  title="Aprobar usuario"
                                >
                                  <CheckCircle size={16} />
                                </button>
                                <button
                                  className="btn-icon btn-icon--danger"
                                  onClick={() => handleRejectUser(u.id)}
                                  title="Rechazar usuario"
                                >
                                  <XCircle size={16} />
                                </button>
                              </>
                            )}
                            <button
                              className="btn-icon"
                              title="Editar usuario"
                              onClick={() => handleEditUser(u)}
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              className="btn-icon btn-icon--danger"
                              title="Eliminar usuario"
                              onClick={() => handleDeleteUser(u.id)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>

        {/* Create Mentor Modal */}
        {showCreateMentorModal && (
          <CreateMentorModal
            onClose={() => setShowCreateMentorModal(false)}
            onSuccess={() => loadUsers()}
          />
        )}

        {/* Create Mentee Modal */}
        {showCreateMenteeModal && (
          <CreateMenteeModal
            onClose={() => setShowCreateMenteeModal(false)}
            onSuccess={() => loadUsers()}
          />
        )}

        {/* Edit User Modal */}
        {showEditModal && selectedUser && (
          <EditUserModal
            user={selectedUser}
            onClose={() => {
              setShowEditModal(false);
              setSelectedUser(null);
            }}
            onSuccess={() => {
              loadUsers();
            }}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default AdminUsers;
